import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useToastMessage } from "../../../../components/ToastMessage/ToastMessage";
import MaterialReactTable, { type MRT_ColumnDef, MRT_ToggleFiltersButton } from "material-react-table";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Textarea,
  Button,
  Tooltip,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Toaster,
} from "@fluentui/react-components";
import {
  FolderFilled,
  DocumentTextRegular,
  MoreVerticalRegular,
  EditRegular,
  DeleteRegular,
  Copy20Regular,
  InfoRegular,
  Dismiss16Regular,
  FolderAddRegular,
  DocumentAddRegular,
} from "@fluentui/react-icons";
import { AppDispatch } from "../../../../redux/store";
import {
  addFileLink,
  updateFileLink,
  deleteFileLink,
  FileLinkItem,
} from "../../../../redux/Document/fileLinkSlice";
import apiServices from "../../../../service";
import FormikInputRequest from "../../../../service/hook/useFormik";
import ASCButton from "../../../../components/Button";
import DynamicDatePicker from "../../../../components/ControlledDatePicker/dynamicDatepicker";
import "../style.css";
import { formatedDate } from "../../../../utils";
import ReactQuillComponent from "../../../../components/ReactQuillComponent";

const PAGE_SIZE = 20;
// Improved sanitize function to catch all quote types
const sanitizePath = (value: string): string =>
  value.replace(/["\u201C\u201D'\u2018\u2019]/g, "").trim();
function resolveType(fileLink: string): "file" | "folder" {
  return /[/\\]{1,2}$/.test(fileLink) ? "folder" : "file";
}

interface FileLinkPanelProps {
  activeCustomerId: string;
  notify: (msg: string, sub: string, type: string) => void;
}

// ── Delete confirmation dialog ────────────────────────────────────────────────
function DeleteConfirmDialog({
  item,
  onConfirm,
  onCancel,
}: {
  item: FileLinkItem | null;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  if (!item) return null;
  const typeLabel = item.type === "folder" ? "Folder" : "File";
  return (
    <Dialog open={!!item}>
      <DialogSurface className="dialog-surface">
        <DialogTitle>Delete {typeLabel}?</DialogTitle>
        <DialogContent>
          Are you sure you want to delete <strong>{item.title}</strong>?
        </DialogContent>
        <DialogActions className="flex-end">
          <ASCButton shape="rounded" onClick={onCancel} label="No" />
          <ASCButton
            shape="rounded"
            appearance="primary"
            onClick={onConfirm}
            className="asc-button-primary"
            label="Yes"
          />
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
}

// ── Row actions menu ──────────────────────────────────────────────────────────
function RowActions({
  item,
  onEdit,
  onDelete,
}: {
  item: FileLinkItem;
  onEdit: (item: FileLinkItem) => void;
  onDelete: (item: FileLinkItem) => void;
}) {
  return (
    <Menu positioning="below-end">
      <MenuTrigger>
        <Button appearance="subtle" icon={<MoreVerticalRegular />} />
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          <MenuItem icon={<EditRegular />} onClick={() => onEdit(item)}>
            Edit
          </MenuItem>
          <MenuItem icon={<DeleteRegular />} onClick={() => onDelete(item)}>
            Delete
          </MenuItem>
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}

// ── Add / Edit dialog ─────────────────────────────────────────────────────────
function FileLinkDialog({
  isOpen,
  editItem,
  mode,
  activeCustomerId,
  onClose,
  onSaved,
}: {
  isOpen: boolean;
  editItem: FileLinkItem | null;
  mode: "folder" | "file";
  activeCustomerId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const dispatch = useDispatch<AppDispatch>();
  const [isSaving, setIsSaving] = useState(false);

  const formik = FormikInputRequest({
    initialValues: {
      title: "",
      description: "",
      fileLink: "",
      createdAt: new Date(),
    },
    enableReinitialize: true,
    onSubmitFn: async (values: any) => {
      setIsSaving(true);
      try {
        let fileLink = values.fileLink;
        if (mode === "folder" && fileLink && !/[/\\]{1,2}$/.test(fileLink)) {
          fileLink = fileLink.includes("\\") ? fileLink + "\\" : fileLink + "/";
        }
        const payload = {
          documentTitle: values.title,
          description: values.description,
          fileLink: values.fileLink,
          customer_id: activeCustomerId,
          itemType: mode,
          createdAt: values.createdAt,
        };

        if (editItem) {
          await dispatch(updateFileLink({ id: editItem.id, data: payload }));
        } else {
          await dispatch(addFileLink({ data: payload }));
        }
        onSaved();
        onClose();
        formik.resetForm();
      } finally {
        setIsSaving(false);
      }
    },
    validate: (values: any) => {
      const errors: any = {};

      if (values.description && values.description.length > 5000) {
        errors.description = "Description must not exceed 5000 characters";
      }

      if (!values.fileLink) {
        errors.fileLink = "File path is required";
      } else if (
        /^\\{3,}/.test(values.fileLink) ||
        (/^\/{3,}/.test(values.fileLink) && !values.fileLink.startsWith("file:///"))
      ) {
        errors.fileLink = "Invalid path format. Path starts with too many separators.";
      } else if (values.fileLink.startsWith("file://") && !values.fileLink.startsWith("file:///")) {
        errors.fileLink = "File URI must use three slashes (e.g. file:///path/to/file).";
      } else if (/^[a-zA-Z]:[^/\\]/.test(values.fileLink)) {
        errors.fileLink = "Windows drive letter must be followed by a separator (e.g. C:\\...).";
      } else {
        const hasTrailingSlash = /[/\\]$/.test(values.fileLink);

        if (mode === "folder") {
          if (/[<>"|?*]/.test(values.fileLink)) {
            errors.fileLink = "Path contains invalid characters (< > \" | ? *).";
          } else if (!hasTrailingSlash) {
            errors.fileLink = "Folder paths must include a trailing slash.";
          }
        } else {
          if (hasTrailingSlash) {
            errors.fileLink = "File paths must not end with '/' or '\\'.";
          } else if (/[<>"|?*]/.test(values.fileLink)) {
            errors.fileLink = "Path contains invalid characters (< > \" | ? *).";
          } else if (!/\.[a-zA-Z0-9]+$/.test(values.fileLink)) {
            errors.fileLink = "File path must include a file extension (e.g. .pdf, .docx).";
          } else if (/[ ]+$/.test(values.fileLink)) {
            errors.fileLink = "Invalid format. Trailing spaces found at the end of the path.";
          }
        }
      }
      return errors;
    },
  });

  useEffect(() => {
    formik.resetForm({
      values: {
        title: editItem?.title ?? "",
        description: editItem?.description ?? "",
        fileLink: editItem?.fileLink ?? "",
        createdAt: (editItem as any)?.createdAt
          ? new Date((editItem as any).createdAt)
          : new Date(),
      },
    });
    setTimeout(() => formik.validateForm(), 0);
  }, [editItem, isOpen]);

  const handleCancel = () => {
    onClose();
    formik.resetForm();
  };

  const dialogTitle = editItem
    ? `Edit ${editItem.type === "folder" ? "Folder" : "File"} Link`
    : mode === "folder"
      ? "Add Folder Link"
      : "Add File Link";

  return (
    <Dialog open={isOpen}>
      <DialogSurface className="dialog-surface">
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          <div className="folder_doc">
            <Field label={mode === "folder" ? "Folder Name" : "File Name"}>
              <Input
                appearance="outline"
                value={formik.values.title}
                onChange={(e) => formik.setFieldValue("title", e.target.value)}
              />
            </Field>
          </div>
          <div className="folder_doc">
            <Field
              label={
                <span className="filelink-label">
                  File Path
                  {formik.errors.fileLink && (
                    <span className="required-item">*</span>
                  )}
                </span>
              }
              validationState={formik.errors.fileLink ? "error" : "none"}
              validationMessage={
                formik.errors.fileLink
                  ? `${formik.errors.fileLink}`
                  : mode === "folder" && formik.values.fileLink && !/[/\\]{1,2}$/.test(formik.values.fileLink)
                    ? "Folder path will automatically end with a separator (/ or \\) on save"
                    : ""
              }
            >
              <Input
                appearance="outline"
                placeholder={
                  mode === "folder"
                    ? "e.g. C:\\Users\\John\\Documents\\ or /home/user/documents/"
                    : "e.g. C:\\Users\\John\\Documents\\file.pdf or file:///home/user/file.pdf"
                }
                value={formik.values.fileLink}
                onChange={(e) => {
                  let val = e.target.value;

                  // 1. Remove quotes immediately on paste/type
                  val = sanitizePath(val);

                  // 2. Logic for Files: Remove trailing slashes or backslashes if mode is "file"
                  if (mode === "file") {
                    val = val.replace(/[/\\]+$/, "");
                  }

                  formik.setFieldValue("fileLink", val);
                }}
                contentAfter={
                  <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    {formik.values.fileLink && (
                      <Dismiss16Regular
                        style={{ cursor: "pointer", color: "#888" }}
                        onClick={() => formik.setFieldValue("fileLink", "")}
                      />
                    )}
                    <Tooltip
                      content="This stores the reference path of the target file or folder. To access it, copy the path and paste it into File Explorer. The corresponding file or folder will open."
                      relationship="description"
                      positioning="below-end"
                    >
                      <InfoRegular style={{ cursor: "help" }} />
                    </Tooltip>
                  </span>
                }
              />
            </Field>
          </div>
          <div className="folder_doc">
            <Field label="Date">
              <DynamicDatePicker
                formik={formik}
                border
                name="createdAt"
                customHandleChange={(e: any) => {
                  formik.setFieldValue(e.target.name, e.target.value);
                }}
              />
            </Field>
          </div>
          <div className="folder_doc">
            <Field
              label="Description"
              validationState={formik.errors.description ? "error" : "none"}
              validationMessage={formik.errors.description ? `${formik.errors.description}` : ""}
            >
              <Textarea
                appearance="outline"
                style={{ backgroundColor: "#fff" }}
                value={formik.values.description}
                onChange={(e) =>
                  formik.setFieldValue("description", e.target.value)
                }
              />
            </Field>
          </div>
        </DialogContent>
        <DialogActions className="flex-end">
          <ASCButton shape="rounded" onClick={handleCancel} label="Cancel" />
          <ASCButton
            shape="rounded"
            appearance="primary"
            onClick={() => formik.handleSubmit()}
            className="asc-button-primary"
            label={isSaving ? "Saving..." : "Save"}
            disabled={Object.keys(formik.errors).length > 0 || isSaving}
          />
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
}

// ── Path cell with copy ───────────────────────────────────────────────────────
function PathCell({
  path,
  notify,
}: {
  path: string;
  notify: (msg: string, sub: string, type: string) => void;
}) {
  const handleCopy = () => {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(path).then(() => {
        notify("Path copied to clipboard", "", "success");
      });
    } else {
      const textarea = document.createElement("textarea");
      textarea.value = path;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      notify("Path copied to clipboard", "", "success");
    }
  };

  return (
    <span className="filelink-path-cell">
      <span className="doc-path-cell">{path}</span>
      <Tooltip content="Copy path" relationship="label">
        <Copy20Regular className="filelink-copy-icon" onClick={handleCopy} />
      </Tooltip>
    </span>
  );
}

// ── Date range filter ─────────────────────────────────────────────────────────
function makeDateFilterFormik(
  value: Date | null,
  onSet: (d: Date | null) => void,
) {
  return {
    values: { date: value },
    errors: {},
    touched: {},
    setFieldValue: (_name: string, val: Date | null) => onSet(val),
    setFieldError: () => { },
  };
}

function DateRangeFilter({ column }: { column: any }) {
  const [from, to] =
    (column.getFilterValue() as [Date | null, Date | null]) ?? [null, null];

  const fromFormik = makeDateFilterFormik(from, (val) =>
    column.setFilterValue((old: [Date | null, Date | null]) => [
      val,
      old?.[1] ?? null,
    ]),
  );
  const toFormik = makeDateFilterFormik(to, (val) =>
    column.setFilterValue((old: [Date | null, Date | null]) => [
      old?.[0] ?? null,
      val,
    ]),
  );

  return (
    <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
      <DynamicDatePicker formik={fromFormik} border name="date" width="140px" />
      <span style={{ fontSize: 12, color: "#888" }}>–</span>
      <DynamicDatePicker formik={toFormik} border name="date" width="140px" />
    </div>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────
export default function FileLinkPanel({
  activeCustomerId,
  notify,
}: FileLinkPanelProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { showToast, toasterId } = useToastMessage();

  const [data, setData] = useState<FileLinkItem[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const offsetRef = useRef(0);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const [search, setSearch] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"folder" | "file">("file");
  const [editItem, setEditItem] = useState<FileLinkItem | null>(null);
  const [deleteItem, setDeleteItem] = useState<FileLinkItem | null>(null);

  const fetchPage = useCallback(async (offset: number, append: boolean) => {
    setIsFetching(true);
    try {
      const res = await apiServices.fileLink.getList(activeCustomerId, offset, PAGE_SIZE);
      const raw: any[] = res.data.data ?? [];
      const items: FileLinkItem[] = raw.map((item) => ({
        ...item,
        type: resolveType(item.fileLink),
      }));
      const total: number = res.data.pagination?.total ?? items.length;

      setData((prev) => {
        const combined = append ? [...prev, ...items] : items;
        combined.sort((a, b) => {
          if (a.type !== b.type) return a.type === "folder" ? -1 : 1;
          return (a.title || "").localeCompare(b.title || "");
        });
        return combined;
      });
      offsetRef.current = offset + items.length;
      setHasMore(offsetRef.current < total);
    } finally {
      setIsFetching(false);
    }
  }, [activeCustomerId]);

  // Initial load / reset
  const resetAndFetch = useCallback(() => {
    offsetRef.current = 0;
    setHasMore(true);
    fetchPage(0, false);
  }, [fetchPage]);

  useEffect(() => {
    resetAndFetch();
  }, [activeCustomerId]);

  const fetchMore = useCallback(() => {
    if (!isFetching && hasMore) {
      fetchPage(offsetRef.current, true);
    }
  }, [isFetching, hasMore, fetchPage]);

  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const { scrollHeight, scrollTop, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 200) {
      fetchMore();
    }
  }, [fetchMore]);

  const handleEdit = (item: FileLinkItem) => {
    setEditItem(item);
    setDialogMode(item.type);
    setDialogOpen(true);
  };

  const openAddDialog = (mode: "folder" | "file") => {
    setEditItem(null);
    setDialogMode(mode);
    setDialogOpen(true);
  };

  const handleDelete = (item: FileLinkItem) => {
    setDeleteItem(item);
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;
    await dispatch(deleteFileLink(deleteItem.id));
    notify("File link deleted", "", "success");
    setDeleteItem(null);
    resetAndFetch();
  };

  const handleSaved = () => {
    notify(editItem ? "File link updated" : "File link added", "", "success");
    resetAndFetch();
  };

  const filteredData = useMemo(() => {
    if (!search.trim()) return data;
    const lower = search.toLowerCase();
    return data.filter((item) => item.title?.toLowerCase().includes(lower));
  }, [data, search]);

  const columns = useMemo<MRT_ColumnDef<FileLinkItem>[]>(
    () => [
      {
        accessorKey: "title",
        header: "Name",
        size: 200,
        Cell: ({ row }) => (
          <div className="doc-name-cell" title={row.original.fileLink}>
            {row.original.type === "folder" ? (
              <FolderFilled primaryFill="#fccc77" fontSize="20px" />
            ) : (
              <DocumentTextRegular primaryFill="#8c8c8c" fontSize="20px" />
            )}
            <span className="doc-item-name">{row.original.title}</span>
          </div>
        ),
      },
      {
        accessorKey: "type",
        header: "Type",
        size: 80,
        Cell: ({ row }) => (row.original.type === "folder" ? "Folder" : "File"),
      },
      {
        accessorKey: "created_at",
        header: "Date",
        size: 120,
        filterFn: (row, columnId, filterValue: [Date | null, Date | null]) => {
          const [from, to] = filterValue ?? [null, null];
          const cellValue = row.getValue<string>(columnId);
          if (!cellValue) return true;
          const cellDate = new Date(cellValue);
          cellDate.setHours(0, 0, 0, 0);
          if (from) {
            const fromDate = new Date(from);
            fromDate.setHours(0, 0, 0, 0);
            if (cellDate < fromDate) return false;
          }
          if (to) {
            const toDate = new Date(to);
            toDate.setHours(23, 59, 59, 999);
            if (cellDate > toDate) return false;
          }
          return true;
        },
        Filter: ({ column }) => <DateRangeFilter column={column} />,
        Cell: ({ row }) => formatedDate(row.original?.created_at) || "—"
      },
      {
        accessorKey: "fileLink",
        header: "Path",
        Cell: ({ row }) => (
          <PathCell path={row.original.fileLink} notify={notify} />
        ),
      },
      {
        accessorKey: "description",
        header: "Description",
        Cell: ({ row }) => <ReactQuillComponent value={row.original.description} />,
      },
      {
        id: "actions",
        header: "Actions",
        size: 90,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <RowActions
            item={row.original}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        ),
      },
    ],
    [showToast],
  );

  return (
    <>
      <Toaster toasterId={toasterId} position="top-end" pauseOnHover timeout={3000} />
      <div className="doc-mgmt-table-wrapper">
        <MaterialReactTable
          columns={columns}
          data={filteredData}
          enableTopToolbar={true}
          enableBottomToolbar={false}
          enableColumnActions={false}
          enableSorting={true}
          enableFilters={true}
          enableColumnFilters={true}
          enablePagination={false}
          enableColumnResizing={false}
          enableStickyHeader={true}
          enableRowSelection={false}
          enableGlobalFilter={false}
          enableDensityToggle={false}
          enableFullScreenToggle={false}
          enableHiding={false}
          renderTopToolbarCustomActions={() => (
            <Input
              appearance="outline"
              placeholder="Search by name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              contentAfter={
                search ? (
                  <Dismiss16Regular
                    style={{ cursor: "pointer", color: "#888" }}
                    onClick={() => setSearch("")}
                  />
                ) : undefined
              }
              style={{ minWidth: 220 }}
            />
          )}
          renderToolbarInternalActions={({ table }) => (
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <MRT_ToggleFiltersButton table={table} />
              <Button
                shape="rounded"
                icon={<FolderAddRegular />}
                onClick={() => openAddDialog("folder")}
              >
                Add Folder
              </Button>
              <Button
                shape="rounded"
                appearance="primary"
                className="asc-button-primary"
                icon={<DocumentAddRegular />}
                onClick={() => openAddDialog("file")}
              >
                Add File Link
              </Button>
            </div>
          )}
          state={{ isLoading: isFetching && data.length === 0, showProgressBars: isFetching && data.length > 0 }}
          muiTableContainerProps={{
            ref: tableContainerRef,
            sx: { maxHeight: "calc(100vh - 300px)", overflow: "auto" },
            onScroll: handleScroll,
          }}
          muiTableHeadCellProps={{
            sx: {
              fontWeight: 600,
              fontSize: "1rem",
              backgroundColor: "#f9f9f9",
              borderBottom: "1px solid #ddd",
              position: "sticky",
              top: 0,
              zIndex: 1,
            },
          }}
          muiTableBodyRowProps={{
            sx: { "&:hover td": { backgroundColor: "#f5f5f5" } },
          }}
          muiTableBodyCellProps={{ sx: { fontSize: "1rem" } }}
          localization={{ noRecordsToDisplay: "No file links found" }}
          initialState={{ density: "compact" }}
        />
      </div>

      <FileLinkDialog
        isOpen={dialogOpen}
        editItem={editItem}
        mode={dialogMode}
        activeCustomerId={activeCustomerId}
        onClose={() => setDialogOpen(false)}
        onSaved={handleSaved}
      />

      <DeleteConfirmDialog
        item={deleteItem}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteItem(null)}
      />
    </>
  );
}
